import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Users, Plus, Search, Filter, ChevronLeft, ChevronRight, Edit, Trash2 } from 'lucide-react';
import PermissionGate from '../../shared/PermissionGate';
import { PERMISSIONS } from '../../../constants/permissionMappings';

const EmployeeTable = ({
  employees,
  statistics,
  pagination,
  filters,
  onFiltersChange,
  onEdit,
  onDelete,
  onCreateClick,
  onPageChange
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'firstName', direction: 'asc' });
  const [perPage, setPerPage] = useState(pagination?.limit || 10);

  // Format date for display
  const formatEmployeeDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  // Get invite status badge — only shown for actionable states.
  // 'accepted' is intentionally omitted: the Active employment status already
  // indicates the employee is fully onboarded, so a redundant badge is unnecessary.
  const getInviteBadge = (invitationStatus) => {
    if (!invitationStatus || invitationStatus === 'not_invited' || invitationStatus === 'accepted') return null;

    const variants = {
      pending: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Invite Pending' },
      expired: { bg: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Invite Expired' }
    };

    return variants[invitationStatus] || null;
  };

  // Get status badge colors
  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || 'inactive';

    const variants = {
      active: 'bg-green-50 text-green-700 border-green-200',
      inactive: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      terminated: 'bg-red-50 text-red-700 border-red-200'
    };

    const dotColors = {
      active: 'bg-green-500',
      inactive: 'bg-yellow-500',
      terminated: 'bg-red-500'
    };

    return {
      badge: variants[statusLower] || variants.inactive,
      dot: dotColors[statusLower] || dotColors.inactive,
      label: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Inactive'
    };
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  // Handle search
  const handleSearchChange = (e) => {
    handleFilterChange('search', e.target.value);
  };

  // Handle per-page change
  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    onFiltersChange({ ...filters, limit: newPerPage });
    onPageChange(1); // Reset to page 1 when changing per-page
  };

  // Handle sort
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  // Sort employees
  const sortedEmployees = useMemo(() => {
    if (!employees || employees.length === 0) return [];

    const sorted = [...employees].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [employees, sortConfig]);

  // Render sort icon
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900">{statistics?.total || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <div className="h-4 w-4 bg-green-600 rounded-full"></div>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{statistics?.active || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <div className="h-4 w-4 bg-yellow-600 rounded-full"></div>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-gray-900">{statistics?.inactive || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                <div className="h-4 w-4 bg-red-600 rounded-full"></div>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Terminated</p>
              <p className="text-2xl font-bold text-gray-900">{statistics?.terminated || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Team members</h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                {pagination?.totalCount || 0} users
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>
          </div>

          {/* Filters */}
          {isFiltersOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={filters?.search || ''}
                    onChange={handleSearchChange}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <select
                  value={filters?.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="terminated">Terminated</option>
                </select>

                <select
                  value={filters?.teamId || ''}
                  onChange={(e) => handleFilterChange('teamId', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Teams</option>
                  <option value="unassigned">Unassigned</option>
                </select>

                <button
                  onClick={() => onFiltersChange({ search: '', status: '', teamId: '', limit: perPage })}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('firstName')}
                >
                  <div className="flex items-center gap-2">
                    Name
                    <SortIcon columnKey="firstName" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('employmentStatus')}
                >
                  <div className="flex items-center gap-2">
                    Status
                    <SortIcon columnKey="employmentStatus" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('jobTitle')}
                >
                  <div className="flex items-center gap-2">
                    Role
                    <SortIcon columnKey="jobTitle" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:hidden xl:table-cell cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-2">
                    Email address
                    <SortIcon columnKey="email" />
                  </div>
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Teams
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {sortedEmployees.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Users className="mx-auto h-12 w-12 text-gray-300" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No employees</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by adding your first employee.</p>
                    <div className="mt-6">
                      <button
                        onClick={onCreateClick}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Employee
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedEmployees.map((employee) => {
                  const status = getStatusBadge(employee.employmentStatus);
                  const inviteBadge = getInviteBadge(employee.invitationStatus);

                  return (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {employee.firstName} {employee.lastName}
                              </span>
                              {inviteBadge && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${inviteBadge.bg}`}>
                                  {inviteBadge.label}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{employee.email || 'No email'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border ${status.badge}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`}></span>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.jobTitle || 'No title'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 md:hidden xl:table-cell">
                        {employee.email || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                          {employee.teamName || 'Unassigned'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-0.5">
                          <PermissionGate permission={PERMISSIONS.employees.edit_all}>
                            <button
                              onClick={() => onEdit(employee)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          {employee.employmentStatus !== 'terminated' && (
                            <PermissionGate permission={PERMISSIONS.employees.delete}>
                              <button
                                onClick={() => onDelete(employee)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Terminate"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-gray-200">
          {sortedEmployees.length === 0 ? (
            <div className="p-6 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No employees</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first employee.</p>
              <div className="mt-6">
                <PermissionGate permission={PERMISSIONS.employees.create}>
                  <button
                    onClick={onCreateClick}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Employee
                  </button>
                </PermissionGate>
              </div>
            </div>
          ) : (
            sortedEmployees.map((employee) => {
              const status = getStatusBadge(employee.employmentStatus);
              const inviteBadge = getInviteBadge(employee.invitationStatus);

              return (
                <div key={employee.id} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex-shrink-0 h-12 w-12">
                        <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-900 truncate">
                            {employee.firstName} {employee.lastName}
                          </h3>
                          {inviteBadge && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border flex-shrink-0 ${inviteBadge.bg}`}>
                              {inviteBadge.label}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{employee.email || 'No email'}</p>
                        <p className="text-sm text-gray-600 mt-1">{employee.jobTitle || 'No title'}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium border ${status.badge}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`}></span>
                      {status.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <p className="text-gray-500">Team</p>
                      <p className="text-gray-900 font-medium">{employee.teamName || 'Unassigned'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Hire Date</p>
                      <p className="text-gray-900 font-medium">{formatEmployeeDate(employee.hireDate)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                    <PermissionGate permission={PERMISSIONS.employees.edit_all}>
                      <button
                        onClick={() => onEdit(employee)}
                        className="flex-1 bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Edit
                      </button>
                    </PermissionGate>
                    {employee.employmentStatus !== 'terminated' && (
                      <PermissionGate permission={PERMISSIONS.employees.delete}>
                        <button
                          onClick={() => onDelete(employee)}
                          className="bg-red-100 text-red-700 text-sm font-medium py-2 px-3 rounded-md hover:bg-red-200 transition-colors"
                        >
                          Terminate
                        </button>
                      </PermissionGate>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 md:px-6 md:pt-3 md:pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Per-page selector */}
              <div className="flex items-center gap-2">
                <label htmlFor="perPage" className="text-sm text-gray-700">Show</label>
                <select
                  id="perPage"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>

              {/* Pagination info and controls */}
              <div className="flex items-center justify-between sm:justify-end gap-4">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of {pagination.totalCount}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onPageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => onPageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeTable;
